import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";
import {
    Smartphone, CreditCard, Monitor, Building, Wallet, Store, BookOpen
} from "lucide-react";

export default function PaymentGuide() {
    const paymentMethods = useMemo(() => [
        {
            id: "wondr",
            title: "Wondr By BNI",
            icon: Smartphone,
            steps: [
                "Open Wondr By BNI and enter your User ID and password;",
                "Select the 'Virtual Account Billing' menu;",
                "Select 'New Destination';",
                "Enter your 16-digit Virtual Account number (e.g., 9881234500001946) in the 'Virtual Account Number' field;",
                "The billing amount will appear on the confirmation screen; select 'Continue';",
                "Confirm the transaction and select 'Transact Now';",
                "Enter your transaction PIN; your payment is successful."
            ]
        },
        {
            id: "atm-bni",
            title: "BNI ATM",
            icon: CreditCard,
            steps: [
                "Insert your card;",
                "Select Language;",
                "Enter your ATM PIN;",
                "Select 'Other Menu';",
                "Select 'Transfer';",
                "Select the account type you wish to use (e.g., 'From Savings Account');",
                "Select 'Virtual Account Billing';",
                "Enter your 16-digit Virtual Account number (e.g., 9881234500001946);",
                "The billing amount will appear on the confirmation screen;",
                "Confirm the details and proceed with the transaction if correct;",
                "Transaction completed."
            ]
        },
        {
            id: "bnidirect",
            title: "BNIDirect",
            icon: Monitor,
            steps: [
                "Go to https://bnidirect.bni.co.id and press 'Enter';",
                "Enter your Company ID, User ID, and Password, then click 'Login';",
                "Select the 'Bill Payment' menu;",
                "Select 'Payment';",
                "Click the radio button in the 'Sender Account' column;",
                "In the 'Recipient' column, select 'New Recipient' and choose 'Institution' as 'VA Billing Open Payment / Closed Payment';",
                "Enter the name in the 'Recipient Name' column;",
                "Input the 16-digit Virtual Account number in the 'VA Number' column (e.g., 9881234500001946);",
                "Click 'Continue', select the 'Instruction Type', and click 'Continue' again;",
                "The Bill Payment Confirmation screen will appear;",
                "Enter the amount or the total required payment in the 'Amount' column;",
                "Confirm and click 'Submit';",
                "The Approver and Releaser users can then authorize the transaction in the 'Task List' menu by entering the Token/OTP Authentication Code;",
                "Your payment is successful."
            ]
        },
        {
            id: "sms-bni",
            title: "BNI SMS Banking",
            icon: Smartphone,
            steps: [
                "Open the BNI SMS Banking application;",
                "Select the 'Transfer' menu;",
                "Select 'Transfer to BNI Account';",
                "Enter the 16-digit Virtual Account number as the destination (e.g., 9881234500001946);",
                "Enter the transfer amount according to your bill. Different amounts cannot be processed;",
                "Select 'Process' and then 'Agree';",
                "Reply to the SMS by typing the PIN as instructed;",
                "Transaction successful."
            ]
        },
        {
            id: "teller-bni",
            title: "BNI Branch / Teller",
            icon: Building,
            steps: [
                "Visit the nearest BNI Branch/Outlet;",
                "Inform the Teller that you wish to make a 'Virtual Account Billing' payment;",
                "Provide your 16-digit Virtual Account number to the Teller (e.g., 9881234500001946);",
                "The Teller will confirm the details with you;",
                "The Teller processes the transaction;",
                "Upon success, you will receive a proof of payment from the Teller."
            ]
        },
        {
            id: "agen46",
            title: "Agen46",
            icon: Store,
            steps: [
                "Visit the nearest Agen46 (kiosk/shop with the Agen46 logo);",
                "Inform the Agent that you wish to make a 'Virtual Account Billing' payment;",
                "Provide your 16-digit Virtual Account number to the Agent;",
                "The Agent will confirm the details with you;",
                "The Agent processes the transaction;",
                "Upon success, you will receive a proof of payment from the Agent."
            ]
        },
        {
            id: "atm-lain",
            title: "Other Bank ATM",
            icon: CreditCard,
            steps: [
                "Insert your card into the ATM machine;",
                "Select 'Other Transactions';",
                "Select the 'Transfer' menu;",
                "Select 'Transfer to Other Bank';",
                "Enter BNI bank code (009) followed by the 16-digit Virtual Account number (e.g., 0099881234500001946);",
                "Enter the transfer amount according to your bill (different amounts cannot be processed);",
                "Review your details on the confirmation screen and press 'Yes' to continue;",
                "Transaction successful."
            ]
        },
        {
            id: "ebanking-lain",
            title: "Other Mobile/e-Banking",
            icon: Smartphone,
            steps: [
                "Select 'Interbank Transfer' or 'Online Interbank Transfer';",
                "Enter BNI bank code (009) or select BNI as the destination bank;",
                "Enter the 16-digit Virtual Account number in the destination account field (e.g., 9881234500001946);",
                "Enter the transfer amount according to your bill (different amounts cannot be processed);",
                "Review your details on the confirmation screen and if correct, complete the transaction;",
                "Transaction successful."
            ]
        },
        {
            id: "teller-lain",
            title: "Other Bank Branch",
            icon: Building,
            steps: [
                "Go to the Teller counter to submit the transfer form using SKN/LLG or RTGS methods;",
                "Ensure the form is filled as follows:\n• Recipient account number: your 16-digit Virtual Account number.\n• Recipient name: as listed on the invoice (e.g., Student Name).\n• Destination Bank: BNI.\n• Amount: the total amount required by your bill.",
                "The Teller processes the transaction;",
                "Upon success, you will receive a proof of payment from the Teller."
            ]
        },
        {
            id: "ovo",
            title: "OVO",
            icon: Wallet,
            steps: [
                "Open the OVO app;",
                "Select the 'Transfer' menu;",
                "Select 'Bank Account';",
                "Enter BNI bank code (009) or select BNI as the destination bank;",
                "Enter the 16-digit Virtual Account number in the destination account field (e.g., 9881234500001946);",
                "Enter the transfer amount according to your bill;",
                "Select 'Transfer';",
                "Review your details on the confirmation screen and select 'Confirm' if correct;",
                "Transaction successful."
            ]
        },
        {
            id: "dana",
            title: "DANA",
            icon: Wallet,
            steps: [
                "Open the DANA app;",
                "Select 'Send' and then 'Send to Bank Account';",
                "Select 'Bank Account';",
                "Enter BNI as the destination bank;",
                "Enter the 16-digit Virtual Account number in the destination account field (e.g., 9881234500001946);",
                "Click 'Add New Bank' and enter the transfer amount according to your bill;",
                "Select 'Confirm';",
                "Review your details on the confirmation screen and select 'Confirm' if correct;",
                "Transaction successful."
            ]
        },
        {
            id: "gopay",
            title: "GoPay",
            icon: Wallet,
            steps: [
                "Open the GoPay app;",
                "Select 'Pay' and then 'to Bank Account';",
                "Select 'Bank Account';",
                "Enter BNI as the destination bank;",
                "Enter the 16-digit Virtual Account number in the destination account field;",
                "Click 'Verify' and 'Continue', then enter the transfer amount according to your bill;",
                "Select 'Confirm';",
                "Review your details on the confirmation screen and confirm;",
                "Transaction successful."
            ]
        },
        {
            id: "doku",
            title: "DOKU",
            icon: Wallet,
            steps: [
                "Open the DOKU app;",
                "Select 'Transfer', then 'Bank Account' and 'Select Bank';",
                "Enter BNI as the destination bank;",
                "Enter the 16-digit Virtual Account number in the destination account field;",
                "Enter the transfer amount according to your bill;",
                "Select 'Confirm';",
                "Review your details on the confirmation screen and select 'Send' if correct;",
                "Transaction successful."
            ]
        }
    ], []);

    return (
        <Card className="border shadow-sm rounded-2xl bg-white dark:bg-slate-900 overflow-hidden pt-0">
            <CardHeader className="bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 md:px-8">
                <CardTitle className="flex items-center gap-3 text-lg md:text-xl font-bold text-slate-800 dark:text-slate-100 pt-8">
                    <BookOpen className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    Virtual Account Payment Instructions
                </CardTitle>
                <CardDescription className="text-sm text-slate-500 dark:text-slate-400">
                    If you choose Virtual Account, please follow the instructions for your preferred platform.
                </CardDescription>
            </CardHeader>

            <CardContent className="p-6 md:p-8">
                <Accordion type="single" collapsible className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {paymentMethods.map((method) => {
                        const Icon = method.icon;
                        return (
                            <AccordionItem key={method.id} value={method.id} className="border-b-slate-100 dark:border-b-slate-800">
                                <AccordionTrigger className="hover:no-underline hover:bg-slate-50 dark:hover:bg-slate-800/50 px-3 rounded-lg transition-colors py-4">
                                    <div className="flex items-center gap-3 text-left">
                                        <div className="p-2 rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                            {method.title}
                                        </span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-3 pb-4 pt-2">
                                    <ol className="list-decimal list-outside pl-5 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                                        {method.steps.map((step, idx) => (
                                            <li key={idx} className="pl-1 leading-relaxed whitespace-pre-line">
                                                {step}
                                            </li>
                                        ))}
                                    </ol>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </CardContent>
        </Card>
    );
}